import React from "react";
import { Alert, Descriptions, Drawer, Space, Tag, Typography } from "antd";
import {
  QrCodeStatuses,
  buildAmountBreakdown,
  describeBillingPeriod,
  describeEmailStatus,
  describePaymentLinkStatus,
  describePaymentStatus,
  describeQrCodeStatus,
  formatPaymentDate,
  resolveQrCodeStatus,
} from "../utils/payments";

//
// The full record for one payment.
//
// Every amount comes from the payment's frozen `billing_snapshot`, so a
// payment opened months later shows exactly the figures its customer was
// quoted, even if the plan has been repriced since.
//
// The raw webhook payloads are not shown. They are provider data an
// administrator has no reason to read record by record, and the API does not
// return them. What is shown instead is the reconciliation note, which is the
// sanitized operator-facing summary of anything that needs a human.
//

const monospace = { fontFamily: "SFMono-Regular, Consolas, Menlo, monospace", fontSize: 12 };

const PaymentDetailsDrawer = ({ open, payment, onClose }) => {
  if (!payment) {
    return <Drawer open={open} onClose={onClose} width={560} title="Payment details" />;
  }

  const snapshot = payment.billing_snapshot || {};
  const paymentStatus = describePaymentStatus(payment.payment_status);
  const linkStatus = describePaymentLinkStatus(payment.payment_link_status);
  const linkEmail = describeEmailStatus(payment.payment_link_email_status);
  const receiptEmail = describeEmailStatus(payment.receipt_email_status);
  // Re-derived client-side, not read verbatim: Razorpay fires no webhook for
  // a QR's auto-timeout, so a row fetched minutes ago can still read
  // "created" after the QR has quietly expired.
  const qrStatus = describeQrCodeStatus(resolveQrCodeStatus(payment));
  const hasQrCode = Boolean(payment.razorpay_qr_code_id);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title="Payment details"
      extra={
        <Space size={4}>
          <Tag color={paymentStatus.color}>{paymentStatus.label}</Tag>
          <Tag color={linkStatus.color}>Link: {linkStatus.label}</Tag>
          {hasQrCode && <Tag color={qrStatus.color}>QR: {qrStatus.label}</Tag>}
        </Space>
      }
    >
      <Space orientation="vertical" size={18} style={{ width: "100%" }}>
        {payment.reconciliation_note && (
          <Alert
            type="warning"
            showIcon
            message="This payment needs attention"
            description={payment.reconciliation_note}
          />
        )}

        <Descriptions
          size="small"
          column={1}
          bordered
          title="Billing"
          items={[
            { key: "customer", label: "Customer", children: payment.customer?.name || snapshot.customer_name || "—" },
            { key: "plan", label: "Plan", children: payment.plan?.name || snapshot.plan_name || "—" },
            { key: "cycle", label: "Billing cycle", children: snapshot.billing_cycle || "—" },
            { key: "period", label: "Billing period", children: describeBillingPeriod(payment) },
            ...buildAmountBreakdown(payment).map((row, index) => ({
              key: `amount-${index}`,
              label: row.label,
              children: row.strong ? <Typography.Text strong>{row.value}</Typography.Text> : row.value,
            })),
            { key: "currency", label: "Currency", children: payment.currency || snapshot.currency || "—" },
          ]}
        />

        <Descriptions
          size="small"
          column={1}
          bordered
          title="Identifiers"
          items={[
            {
              key: "reference",
              label: "Payment reference",
              children: (
                <Typography.Text copyable style={monospace}>
                  {payment.reference_id}
                </Typography.Text>
              ),
            },
            {
              key: "link-id",
              label: "Razorpay link ID",
              children: payment.razorpay_payment_link_id ? (
                <Typography.Text copyable style={monospace}>
                  {payment.razorpay_payment_link_id}
                </Typography.Text>
              ) : (
                "—"
              ),
            },
            {
              key: "payment-id",
              label: "Razorpay payment ID",
              children: payment.razorpay_payment_id ? (
                <Typography.Text copyable style={monospace}>
                  {payment.razorpay_payment_id}
                </Typography.Text>
              ) : (
                "—"
              ),
            },
            ...(payment.razorpay_order_id
              ? [
                  {
                    key: "order-id",
                    label: "Razorpay order ID",
                    children: (
                      <Typography.Text copyable style={monospace}>
                        {payment.razorpay_order_id}
                      </Typography.Text>
                    ),
                  },
                ]
              : []),
          ]}
        />

        <Descriptions
          size="small"
          column={1}
          bordered
          title="Status"
          items={[
            {
              key: "payment-status",
              label: "Payment",
              children: <Tag color={paymentStatus.color}>{paymentStatus.label}</Tag>,
            },
            {
              key: "link-status",
              label: "Payment link",
              children: <Tag color={linkStatus.color}>{linkStatus.label}</Tag>,
            },
            {
              key: "link-url",
              label: "Hosted payment page",
              children: payment.payment_link_url ? (
                <Typography.Link href={payment.payment_link_url} target="_blank" rel="noreferrer">
                  Open link
                </Typography.Link>
              ) : (
                "—"
              ),
            },
            {
              key: "expires",
              label: "Link expires",
              children: formatPaymentDate(payment.payment_link_expires_at, { withTime: true }),
            },
            {
              key: "captured",
              label: "Payment verified at",
              children: formatPaymentDate(payment.captured_at, { withTime: true }),
            },
            ...(payment.captured_via
              ? [
                  {
                    key: "captured-via",
                    label: "Paid via",
                    // The two mechanisms are offered together for the same
                    // amount; this is the only record of which one the
                    // customer actually used.
                    children: payment.captured_via === "qr_code" ? "QR code (UPI)" : "Payment link",
                  },
                ]
              : []),
            {
              key: "renewed",
              label: "Subscription renewed at",
              children: formatPaymentDate(payment.subscription_renewed_at, { withTime: true }),
            },
          ]}
        />

        {hasQrCode && (
          <Descriptions
            size="small"
            column={1}
            bordered
            title="QR Code (UPI)"
            items={[
              {
                key: "qr-status",
                label: "Status",
                children: <Tag color={qrStatus.color}>{qrStatus.label}</Tag>,
              },
              {
                key: "qr-image",
                // A direct hotlink is fine here, unlike in the email: this is
                // the admin console, not an email client that blocks remote
                // images by default.
                label: "QR image",
                children: payment.qr_code_image_url ? (
                  <Typography.Link href={payment.qr_code_image_url} target="_blank" rel="noreferrer">
                    Open QR image
                  </Typography.Link>
                ) : (
                  "—"
                ),
              },
              {
                key: "qr-expires",
                label: "QR expires",
                children: (
                  <Space orientation="vertical" size={0}>
                    <span>{formatPaymentDate(payment.qr_code_expires_at, { withTime: true })}</span>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Separate from the link&apos;s own expiry above -- Razorpay caps a QR code at 2
                      hours.
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                key: "qr-closed",
                label: "QR closed at",
                children: formatPaymentDate(payment.qr_code_closed_at, { withTime: true }),
              },
            ]}
          />
        )}

        {resolveQrCodeStatus(payment) === QrCodeStatuses.CANCELLED && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            This QR code was cancelled and is not regenerated automatically -- unlike an expired one,
            which Resend Payment Link replaces. Generate a new payment link to offer a fresh QR code.
          </Typography.Text>
        )}

        <Descriptions
          size="small"
          column={1}
          bordered
          title="Email"
          items={[
            {
              key: "link-email",
              label: "Payment link email",
              children: (
                <Space size={6} wrap>
                  <Tag color={linkEmail.color}>{linkEmail.label}</Tag>
                  <Typography.Text type="secondary">
                    {payment.payment_link_email_attempts || 0} attempt
                    {Number(payment.payment_link_email_attempts) === 1 ? "" : "s"}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              key: "link-email-sent",
              label: "Link emailed at",
              children: formatPaymentDate(payment.payment_link_sent_at, { withTime: true }),
            },
            ...(payment.payment_link_email_error
              ? [
                  {
                    key: "link-email-error",
                    label: "Last email error",
                    children: (
                      <Typography.Text type="danger">{payment.payment_link_email_error}</Typography.Text>
                    ),
                  },
                ]
              : []),
            {
              key: "receipt-email",
              label: "Receipt email",
              children: (
                <Space size={6} wrap>
                  <Tag color={receiptEmail.color}>{receiptEmail.label}</Tag>
                  <Typography.Text type="secondary">
                    {payment.receipt_email_attempts || 0} attempt
                    {Number(payment.receipt_email_attempts) === 1 ? "" : "s"}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              key: "receipt-sent",
              label: "Receipt emailed at",
              children: formatPaymentDate(payment.receipt_email_sent_at, { withTime: true }),
            },
            ...(payment.receipt_email_error
              ? [
                  {
                    key: "receipt-email-error",
                    label: "Last receipt error",
                    children: <Typography.Text type="danger">{payment.receipt_email_error}</Typography.Text>,
                  },
                ]
              : []),
          ]}
        />

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Amounts are shown exactly as they were quoted to the customer. &quot;Sent&quot; means the
          email was accepted for delivery, not that it was opened.
        </Typography.Text>
      </Space>
    </Drawer>
  );
};

export default PaymentDetailsDrawer;
